import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, EntityType, Persisted } from '@tunasong/schemas'
import { isBandSpace } from '@tunasong/schemas'
import { allQueries, useAdvancedSearch } from '@tunasong/search'
import { findBand } from '@tunasong/ui-layouts'
import { useHomeSpaces } from '../views/use-home-spaces.js'

/**
 * The bands on the Notifications tab (GH #2814 M2, N4): one per id the server reads a calendar's notice setting under,
 * the nearest band space above the calendar (GH #2970).
 * - The top-level spaces that are bands: a band space, or a Space shared with others (whose calendar sits in it).
 * - Every band space I own or that is shared with me, however deep: a band space made inside a Space keeps its
 *   calendar, so its setting is stored under its own id, not the Space's.
 * - The band space a link names (`?band=`), read by its id: the search index can lag one made a moment ago.
 */
export const notificationBands = ({
  spaces,
  bandSpaces,
  linked,
}: {
  spaces: Persisted<Entity>[]
  bandSpaces: Persisted<Entity>[]
  linked?: Persisted<Entity>
}) => {
  const top = spaces.filter(space => findBand([space]))
  const nested = [...bandSpaces, ...(linked ? [linked] : [])].filter(isBandSpace).filter(space => !space.trash)
  return [...top, ...nested].filter((space, i, all) => all.findIndex(other => other.id === space.id) === i)
}

const bandName = (band: Persisted<Entity>) => band.name?.trim() || 'Untitled band'

/** The space a band sits in, from a search result's `parentChain` (nearest parent first) */
const parentName = (band: Persisted<Entity>) => {
  const chain = (band as { parentChain?: { name?: string }[] }).parentChain
  return chain?.[0]?.name?.trim() || undefined
}

/**
 * Each band's heading, by id (Joni's audit of M1c/M2, finding 10; approved by Brian): its name, and when another band
 * has the same name, the space it sits in, "New Space (in Music)". A band with no space to name keeps its name.
 */
export const bandHeadings = (bands: Persisted<Entity>[]) => {
  const counts = new Map<string, number>()
  for (const band of bands) {
    counts.set(bandName(band), (counts.get(bandName(band)) ?? 0) + 1)
  }
  return Object.fromEntries(
    bands.map(band => {
      const name = bandName(band)
      const parent = (counts.get(name) ?? 0) > 1 ? parentName(band) : undefined
      return [band.id, parent ? `${name} (in ${parent})` : name]
    })
  )
}

/** The most band spaces the tab lists: far more than anyone is in */
const maxBandSpaces = 500

export const useNotificationBands = (linkedId?: string | null) => {
  const { spaces, loading } = useHomeSpaces()
  /**
   * Band spaces at any depth come from the search: `GET entities/` returns only top-level ones (GH #2970). Search
   * returns what I own or is shared with me, not public entities, and nothing from the trash.
   */
  const types: EntityType[] = ['bandspace']
  const { results } = useAdvancedSearch(allQueries.type({ types, size: maxBandSpaces }), {
    queryType: 'type',
    size: maxBandSpaces,
    intent: { types, size: maxBandSpaces },
  })
  const { entity: linked } = graphHooks.useEntity(linkedId ?? undefined)
  const bands = notificationBands({ spaces, bandSpaces: results as Persisted<Entity>[], linked })
  return { bands, loading }
}
